import {
	ReactElement,
	Ref,
	useCallback,
	useRef,
	useState
} from "react";
import { WeekView, WeekViewCardStyle } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	generateTrackingId,
	isNotNil,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { ResultMapperColor } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { PlanningItemModel } from "./PlanningLiveTileContent";
import { PlanningItemDetails } from "./PlanningItemDetails";

export type PlanningCardProps = {
	item: PlanningItemModel;
	appName: string;
	timezone: TimeZone;
};

export const PlanningCard = (props: PlanningCardProps): ReactElement => {
	const {
		item,
		appName,
		timezone
	} = props;

	const {
		title,
		subtitle1
	} = item;

	const [isViewingDetails, setIsViewingDetails] = useState<boolean>(false);

	const ref: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);

	const clickHandler = useCallback(
		(): void => {
			setIsViewingDetails(true);
		},
		[]
	);

	const closeHandler = useCallback(
		(): void => {
			setIsViewingDetails(false);
		},
		[]
	);

	const titleValue: string | undefined = title?.value;

	const computedTitle: string = isNotNilOrWhiteSpace(titleValue)
		? titleValue
		: localize("_NoTitle");

	return (
		<>
			<WeekView.Card
				ref={ref}
				onClick={clickHandler}
				isSelected={isViewingDetails}
				style={
					isNotNil(title?.color)
						? colorToCardStyleMap[title.color]
						: undefined
				}
				title={computedTitle}
				trackingId={generateTrackingId("planning-card", `|${item.startDateTime.value}`)}
			>
				<WeekView.Card.Title>
					{computedTitle}
				</WeekView.Card.Title>
				{isNotNilOrWhiteSpace(subtitle1?.value) && (
					<WeekView.Card.Info>
						{subtitle1.value}
					</WeekView.Card.Info>
				)}
			</WeekView.Card>

			{isViewingDetails && ref.current && (
				<PlanningItemDetails
					item={item}
					appName={appName}
					target={ref.current}
					onClose={closeHandler}
					timezone={timezone}
				/>
			)}
		</>
	);
};
PlanningCard.displayName = "PlanningCard";

const colorToCardStyleMap: Record<ResultMapperColor, WeekViewCardStyle> = {
	[ResultMapperColor.Green]: "success",
	[ResultMapperColor.Blue]: "info",
	[ResultMapperColor.Orange]: "warning",
	[ResultMapperColor.Red]: "danger",
	[ResultMapperColor.Grey]: "secondary",
	[ResultMapperColor.Default]: "default",
	[ResultMapperColor.Purple]: "calendar-6",
	[ResultMapperColor.Cyan]: "calendar-9"
};
